import { useEffect, useState } from 'react';
import { clientFetch } from './api';
import ClientShell from './ClientShell';

const styles = {
  container: {
    padding: '24px',
    maxWidth: '800px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#FFF',
    marginBottom: '16px',
    marginTop: '32px',
  },
  formSection: {
    backgroundColor: '#111827',
    border: '1px solid #1F2937',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '24px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#9CA3AF',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#0B1120',
    border: '1px solid #1F2937',
    borderRadius: '6px',
    color: '#FFF',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  inputReadOnly: {
    backgroundColor: '#0B1120',
    border: '1px solid #1F2937',
    color: '#6B7280',
    cursor: 'not-allowed',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  button: {
    backgroundColor: '#3B82F6',
    color: '#FFF',
    border: 'none',
    padding: '10px 24px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.2s',
    marginRight: '8px',
  },
  buttonSecondary: {
    backgroundColor: '#1F2937',
    color: '#FFF',
    border: '1px solid #374151',
  },
  buttonGroup: {
    display: 'flex',
    gap: '8px',
  },
  successMessage: {
    backgroundColor: '#ecfdf5',
    color: '#065f46',
    padding: '12px 16px',
    borderRadius: '6px',
    marginBottom: '16px',
    fontSize: '14px',
    fontWeight: '500',
  },
  errorMessage: {
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    padding: '12px 16px',
    borderRadius: '6px',
    marginBottom: '16px',
    fontSize: '14px',
    fontWeight: '500',
  },
  readOnlyBox: {
    backgroundColor: '#0B1120',
    border: '1px solid #1F2937',
    borderRadius: '6px',
    padding: '12px',
    color: '#9CA3AF',
    fontSize: '14px',
  },
  loadingText: {
    color: '#9CA3AF',
    fontSize: '14px',
  },
};

export default function ClientProfile() {
  const [profileData, setProfileData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await clientFetch('/client/portal/profile');
        setProfileData(data);
        setEditData({
          contact_name: data.contact_name,
          contact_phone: data.contact_phone,
          contact_title: data.contact_title,
          company_domain: data.company_domain,
        });
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load profile');
        console.error('Profile error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!editData.contact_name || !editData.contact_phone) {
      setError('Contact name and phone are required');
      return;
    }

    try {
      setSaving(true);
      const updated = await clientFetch('/client/portal/profile', {
        method: 'PUT',
        body: JSON.stringify(editData),
      });
      setProfileData(updated);
      setMessage('Profile updated successfully');
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError('Failed to update profile');
      console.error('Profile update error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!passwordData.currentPassword || !passwordData.newPassword) {
      setError('All password fields are required');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    try {
      setChangingPassword(true);
      await clientFetch('/client/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        }),
      });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setMessage('Password changed successfully');
      setError(null);
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to change password');
      console.error('Password change error:', err);
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <ClientShell title="Profile">
        <div style={styles.container}>
          <p style={styles.loadingText}>Loading profile...</p>
        </div>
      </ClientShell>
    );
  }

  if (!profileData) {
    return (
      <ClientShell title="Profile">
        <div style={styles.container}>
          <p style={styles.loadingText}>Profile not available</p>
        </div>
      </ClientShell>
    );
  }

  return (
    <ClientShell title="Profile">
      <div style={styles.container}>
        {message && <div style={styles.successMessage}>{message}</div>}
        {error && <div style={styles.errorMessage}>{error}</div>}

        {/* Company Information */}
        <div style={styles.sectionTitle}>Company Information</div>
        <div style={styles.formSection}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Company Name</label>
              <div style={styles.readOnlyBox}>{profileData.company_name}</div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Service Tier</label>
              <div style={styles.readOnlyBox}>{profileData.tier || 'Standard'}</div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div style={styles.sectionTitle}>Contact Information</div>
        <form style={styles.formSection} onSubmit={handleProfileUpdate}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Contact Name</label>
              <input
                style={styles.input}
                type="text"
                value={editData?.contact_name || ''}
                onChange={(e) =>
                  setEditData({ ...editData, contact_name: e.target.value })
                }
                placeholder="Your full name"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Contact Title</label>
              <input
                style={styles.input}
                type="text"
                value={editData?.contact_title || ''}
                onChange={(e) =>
                  setEditData({ ...editData, contact_title: e.target.value })
                }
                placeholder="Your job title"
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Phone Number</label>
              <input
                style={styles.input}
                type="tel"
                value={editData?.contact_phone || ''}
                onChange={(e) =>
                  setEditData({ ...editData, contact_phone: e.target.value })
                }
                placeholder="Your phone number"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Company Domain</label>
              <input
                style={styles.input}
                type="text"
                value={editData?.company_domain || ''}
                onChange={(e) =>
                  setEditData({ ...editData, company_domain: e.target.value })
                }
                placeholder="company.com"
              />
            </div>
          </div>

          <div style={styles.buttonGroup}>
            <button
              style={styles.button}
              type="submit"
              disabled={saving}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#3B82F6';
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        {/* Change Password */}
        <div style={styles.sectionTitle}>Security</div>
        <form style={styles.formSection} onSubmit={handlePasswordChange}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Current Password</label>
            <input
              style={styles.input}
              type="password"
              value={passwordData.currentPassword}
              onChange={(e) =>
                setPasswordData({
                  ...passwordData,
                  currentPassword: e.target.value,
                })
              }
              placeholder="Enter your current password"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>New Password</label>
            <input
              style={styles.input}
              type="password"
              value={passwordData.newPassword}
              onChange={(e) =>
                setPasswordData({ ...passwordData, newPassword: e.target.value })
              }
              placeholder="Enter new password (min. 8 characters)"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Confirm Password</label>
            <input
              style={styles.input}
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) =>
                setPasswordData({
                  ...passwordData,
                  confirmPassword: e.target.value,
                })
              }
              placeholder="Confirm new password"
            />
          </div>

          <div style={styles.buttonGroup}>
            <button
              style={styles.button}
              type="submit"
              disabled={changingPassword}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#3B82F6';
              }}
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </ClientShell>
  );
}
